import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppBar, Avatar, BottomNavigation, BottomNavigationAction, Box, IconButton, Menu, MenuItem, Toolbar, Typography } from '@mui/material';
import { Flag, EventNote, Dashboard, CalendarMonth, PointOfSale, People, Inventory, Autorenew, Assessment, AccountBalance, PersonSearch } from '@mui/icons-material';
import { RootState, AppDispatch } from '../store';
import { logout } from '../store/authSlice';
import { Brand } from '../components/Brand';

const NAV_ITEMS = [
  { value: '/dashboard', label: 'Dashboard', icon: <Dashboard fontSize="small" /> },
  { value: '/calendar', label: 'Calendario', icon: <CalendarMonth fontSize="small" /> },
  { value: '/opportunities', label: 'Pipeline', icon: <Flag fontSize="small" /> },
  { value: '/pos', label: 'Punto de Venta', icon: <PointOfSale fontSize="small" /> },
  { value: '/caja', label: 'Caja', icon: <AccountBalance fontSize="small" /> },
  { value: '/sales', label: 'Ventas', icon: <PointOfSale fontSize="small" /> },
  { value: '/activities', label: 'Actividades', icon: <EventNote fontSize="small" /> },
  { value: '/customers', label: 'Clientes', icon: <People fontSize="small" /> },
  { value: '/prospects', label: 'Prospectos', icon: <PersonSearch fontSize="small" /> },
  { value: '/inventory', label: 'Inventario', icon: <Inventory fontSize="small" /> },
  { value: '/replenishment', label: 'Reabastec.', icon: <Autorenew fontSize="small" /> },
  { value: '/reports', label: 'Reportes', icon: <Assessment fontSize="small" /> },
  { value: '/cash-flow', label: 'Flujo Caja', icon: <AccountBalance fontSize="small" /> },
];

export const SellerLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user, tenant } = useSelector((state: RootState) => state.auth);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [currentNav, setCurrentNav] = useState(NAV_ITEMS[0].value);

  useEffect(() => {
    const match = NAV_ITEMS.find((item) => location.pathname.startsWith(item.value));
    setCurrentNav(match?.value || NAV_ITEMS[0].value);
  }, [location.pathname]);

  const handleNavChange = (_: React.SyntheticEvent, value: string) => {
    setCurrentNav(value);
    navigate(value);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
    setMenuAnchor(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <AppBar position="sticky" color="primary">
        <Toolbar>
          <Brand size="sm" color="#fff" />
          <Box sx={{ ml: 2, flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>{tenant?.name || 'CRM Ventas'}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>{user?.name}</Typography>
          </Box>
          <IconButton color="inherit" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32 }}>{user?.name?.charAt(0)?.toUpperCase() || 'V'}</Avatar>
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            <MenuItem onClick={handleLogout}>Cerrar sesión</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1, px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
        <Outlet />
      </Box>

      <BottomNavigation
        showLabels
        value={currentNav}
        onChange={handleNavChange}
        sx={{
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          position: { xs: 'sticky', md: 'sticky' },
          bottom: 0,
          bgcolor: 'background.paper',
          zIndex: 2,
        }}
      >
        {NAV_ITEMS.map((item) => (
          <BottomNavigationAction key={item.value} value={item.value} label={item.label} icon={item.icon} />
        ))}
      </BottomNavigation>
    </Box>
  );
};
